import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import {
  EmojiEvents as TrophyIcon,
  ScheduleOutlined as ClockIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  OpenInNew as LaunchIcon,
  CasinoOutlined as RevolverIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import {
  LOTTERY_TYPES,
  lotteryTypeMapper,
  getExplorerAddressUrl,
} from "../../../../../static/Constants";
import { CommonChip } from "../../../../Components/ui";

const NoWinnerAddress = "0x0000000000000000000000000000000000000000";

const ResultsCard = ({ lottery }) => {
  const [copied, setCopied] = useState(false);

  const lotteryId = lottery?.lotteryId;
  const typeId = Number(lottery?.lotteryType || 0);

  const gameTypeConfig = LOTTERY_TYPES[typeId] || {
    name: lotteryTypeMapper[typeId] || `Lottery ${typeId}`,
    color: colors.primary.main,
    bgColor: colors.blue[50],
    icon: RevolverIcon,
    gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
  };

  const TypeIcon = gameTypeConfig.icon || RevolverIcon;

  const winnerAddress = lottery?.lotteryWinner;
  const hasWinner =
    winnerAddress &&
    winnerAddress !== NoWinnerAddress &&
    winnerAddress !== "" &&
    winnerAddress !== "0x0";

  const drawDateFormatted = useMemo(() => {
    const ts = lottery?.drawTime || lottery?.deployedOn;
    if (!ts || ts === "0") return "Draw Completed";
    return moment.unix(Number(ts)).format("DD MMM YYYY, hh:mm A");
  }, [lottery?.drawTime, lottery?.deployedOn]);

  const handleCopyWinner = (e) => {
    e.stopPropagation();
    if (!winnerAddress) return;
    navigator.clipboard.writeText(winnerAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const truncatedWinner = winnerAddress && winnerAddress.length > 12
    ? `${winnerAddress.slice(0, 8)}...${winnerAddress.slice(-6)}`
    : winnerAddress;

  const explorerUrl = winnerAddress ? getExplorerAddressUrl(winnerAddress) : "";

  return (
    <Card
      className="results-lottery-card"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: gameTypeConfig.color,
          boxShadow: "0 12px 28px rgba(15, 23, 42, 0.08)",
          transform: "translateY(-4px)",
        },
      }}
    >
      {/* Top Accent Gradient Bar */}
      <Box
        sx={{
          height: 4,
          background: gameTypeConfig.gradient,
        }}
      />

      <Box sx={{ p: 2.75, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Header Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          {/* Game Type & ID Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadiusPx.md,
                bgcolor: gameTypeConfig.bgColor,
                color: gameTypeConfig.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TypeIcon sx={{ fontSize: 18 }} />
            </Box>
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: gameTypeConfig.color,
                  lineHeight: 1.1,
                  fontSize: "0.85rem",
                }}
              >
                {gameTypeConfig.name} #{lotteryId}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  display: "flex",
                  alignItems: "center",
                  gap: 0.4,
                  fontSize: "0.7rem",
                }}
              >
                <ClockIcon sx={{ fontSize: 11 }} />
                {drawDateFormatted}
              </Typography>
            </div>
          </Stack>

          {/* Status Badge */}
          <CommonChip
            label="Draw Completed"
            variant="active"
            size="small"
          />
        </Stack>

        {/* Winner Highlight Box */}
        <Box
          sx={{
            p: 2,
            borderRadius: borderRadiusPx.lg,
            bgcolor: hasWinner ? colors.amber[50] : colors.slate[50],
            border: `1px solid ${hasWinner ? colors.amber[200] : colors.slate[200]}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: borderRadiusPx.xs,
                bgcolor: hasWinner ? colors.amber[500] : colors.slate[400],
                color: colors.neutral.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TrophyIcon sx={{ fontSize: 15 }} />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: hasWinner ? colors.amber[800] : colors.slate[600],
                textTransform: "uppercase",
                letterSpacing: 0.6,
                fontSize: "0.72rem",
              }}
            >
              {hasWinner ? "Winning Wallet" : "Outcome Status"}
            </Typography>
          </Stack>

          {hasWinner ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                bgcolor: colors.neutral.white,
                p: 1.25,
                borderRadius: borderRadiusPx.md,
                border: `1px solid ${colors.amber[200]}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: colors.slate[900],
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                }}
              >
                {truncatedWinner}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={handleCopyWinner}
                  sx={{ color: copied ? colors.emerald[600] : colors.slate[500], p: 0.5 }}
                >
                  {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                {explorerUrl && (
                  <IconButton
                    size="small"
                    component="a"
                    href={explorerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: colors.slate[500], p: 0.5 }}
                  >
                    <LaunchIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: colors.slate[500], fontWeight: 500, fontSize: "0.8rem" }}
            >
              No winning ticket selected. Prize pool rolled over to next round.
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

ResultsCard.propTypes = {
  lottery: PropTypes.object.isRequired,
};

export default ResultsCard;
